import { useEffect, useRef, useState } from 'react';

import type { ConnectionStatus } from '@/api/BridgeApi.ts';
import { useExitAnimation } from '@/hooks/useExitAnimation.ts';
import { cn } from '@/utils/cn.ts';

// Matches the green used by the onboarding success components. There is no
// Tailwind token for it, so it's applied inline the same way they do it.
const ACCENT = '#2D5F4F';

// Wait this long after the connection drops before showing anything. Most
// drops recover within a second; surfacing a banner for those just flickers.
const SHOW_DELAY_MS = 2000;

// How long the "Reconnected" confirmation lingers before it slides away.
const RECOVERY_HOLD_MS = 1500;

export const ConnectionStatusBanner = ({
	status,
}: {
	status: ConnectionStatus;
}) => {
	const degraded = status !== 'connected';

	const [showDelayElapsed, setShowDelayElapsed] = useState(false);
	const [recovered, setRecovered] = useState(false);

	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Tracks whether the pill has actually been on screen this outage, so a sub-
	// 2s blip (which never showed anything) doesn't trigger a "Reconnected" moment.
	const wasVisibleRef = useRef(false);

	// Reduced-motion is read once at mount; we deliberately don't listen for
	// changes, matching the app's one-shot reads elsewhere.
	const [prefersReducedMotion] = useState(
		() =>
			typeof window !== 'undefined' &&
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches,
	);

	// The bridge re-renders every consumer on each incoming message, not just on
	// status changes, so this effect is keyed on `degraded` — keying it on the
	// render itself would re-arm the timer on unrelated traffic and the 2s
	// hysteresis would never elapse during a real outage.
	useEffect(() => {
		if (degraded) {
			showTimerRef.current = setTimeout(() => {
				setShowDelayElapsed(true);
			}, SHOW_DELAY_MS);
			return () => {
				if (showTimerRef.current) clearTimeout(showTimerRef.current);
			};
		}
		// Connection restored before (or after) the pill showed: drop back to the
		// hidden baseline so the next outage starts its delay from scratch.
		setShowDelayElapsed(false);
	}, [degraded]);

	const pillShowing = showDelayElapsed && degraded;

	useEffect(() => {
		if (pillShowing) {
			wasVisibleRef.current = true;
		}
	}, [pillShowing]);

	useEffect(() => {
		if (status === 'connected') {
			// Only celebrate a recovery if the user actually saw the outage banner.
			if (wasVisibleRef.current) {
				setRecovered(true);
				recoveryTimerRef.current = setTimeout(() => {
					setRecovered(false);
					wasVisibleRef.current = false;
				}, RECOVERY_HOLD_MS);
				return () => {
					if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
				};
			}
			setRecovered(false);
			return;
		}
		// Outage resumed while a recovery confirmation was pending: cancel it.
		setRecovered(false);
		if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
	}, [status]);

	const isOpen = pillShowing || recovered;
	const { shouldRender, isClosing } = useExitAnimation(isOpen);

	// With reduced motion the pill appears/disappears in its final state with no
	// slide, so it unmounts immediately on close instead of holding for the exit.
	const visible = prefersReducedMotion ? isOpen : shouldRender;
	if (!visible) {
		return null;
	}

	// A pill only ever closes after passing through the recovery hold, so an exit
	// animation always belongs to the success state — keep showing "Reconnected"
	// while it slides out rather than flashing the reconnecting copy.
	const showSuccess = recovered || isClosing;

	const reconnectingText =
		status === 'connecting' ? 'Reconnecting…' : 'Trying to reconnect…';

	const animationClass = prefersReducedMotion
		? ''
		: isClosing
			? 'animate-connection-banner-out'
			: 'animate-connection-banner-in';

	return (
		<div
			className="pointer-events-none absolute inset-x-0 top-3 z-20 flex
				justify-center px-4"
		>
			<div
				role="status"
				aria-live="polite"
				className={cn(
					`pointer-events-auto flex max-w-md items-center gap-2 rounded-full
					border border-(--glass-border) bg-white/90 px-4 py-2 text-sm
					text-red-600 shadow-[var(--shadow-card)] backdrop-blur-md`,
					animationClass,
				)}
				style={showSuccess ? { color: ACCENT } : undefined}
			>
				<span
					className="animate-pulse-dot h-2 w-2 shrink-0 rounded-full bg-red-600"
					style={showSuccess ? { background: ACCENT } : undefined}
				/>
				<span>
					{showSuccess
						? 'Reconnected'
						: `${reconnectingText} Changes will sync once you're reconnected.`}
				</span>
			</div>
		</div>
	);
};
